import type { FC, ReactNode } from 'react'

import { useRef } from 'react'
import { cn } from '@/lib/utils'
import { useInView } from '@/hooks/use-in-view'
import { Reveal } from '@/components/generals/reveal'
import { Container } from '@/components/generals/container'
import { RevealText } from '@/components/generals/reveal-text'
import { SilkDrift } from '@/components/dots/silk-drift'
import { SectionLabel } from '@/components/landing/section-label'
import { PROCESS_STEPS } from '@/constants/content'

const STEP_DELAY_MS = 350

export const ProcessSection: FC = (): ReactNode => {
    const listRef = useRef<HTMLOListElement>(null)
    const inView = useInView(listRef, { rootMargin: '0px 0px -20% 0px' })

    return (
        <section id='process' className='relative isolate border-t border-line bg-white'>
            <SilkDrift />

            <Container className='py-24 md:py-36'>
                <div className='grid gap-10 lg:grid-cols-12 lg:items-end'>
                    <div className='lg:col-span-8'>
                        <SectionLabel index='04'>Process</SectionLabel>

                        <RevealText
                            lines={["A simple way of working,", "run by senior people."]}
                            className='mt-8 text-[clamp(2.5rem,5.4vw,5rem)] font-medium leading-[0.95] tracking-[-0.045em] text-ink'
                        />
                    </div>

                    <Reveal className='lg:col-span-4'>
                        <p className='text-lg leading-relaxed text-ink/70'>
                            Fixed scopes, weekly demos and one accountable lead. You always know what ships next and why.
                        </p>
                    </Reveal>
                </div>

                <ol ref={listRef} className='mt-16 grid gap-14 md:mt-24 md:grid-cols-2 md:gap-x-10 lg:grid-cols-4 lg:gap-0'>
                    {PROCESS_STEPS.map((step, index) => {
                        const delay = { transitionDelay: `${index * STEP_DELAY_MS}ms` }

                        return (
                            <li key={step.number} className='relative pt-10 lg:pr-10'>
                                <span aria-hidden='true' className='absolute inset-x-0 top-0 h-px bg-line' />

                                <span
                                    aria-hidden='true'
                                    style={delay}
                                    className={cn(
                                        'absolute inset-x-0 top-0 h-px origin-left bg-azure-500 transition-transform duration-[900ms] ease-out motion-reduce:transition-none',
                                        inView ? 'scale-x-100' : 'scale-x-0'
                                    )}
                                />

                                <span
                                    aria-hidden='true'
                                    style={delay}
                                    className={cn(
                                        'absolute -top-1 left-0 size-[9px] border transition-colors duration-500 motion-reduce:transition-none',
                                        inView ? 'border-azure-500 bg-azure-500' : 'border-ink/20 bg-white'
                                    )}
                                />

                                <Reveal delay={index * 120}>
                                    <div className='flex items-baseline justify-between gap-4'>
                                        <span className='font-mono text-[11px] tracking-[0.14em] text-muted-foreground'>
                                            {step.number}
                                        </span>
                                        <span className='font-mono text-[11px] uppercase tracking-[0.14em] text-azure-600'>
                                            {step.duration}
                                        </span>
                                    </div>

                                    <h3 className='mt-6 text-3xl font-medium tracking-[-0.035em] text-ink'>
                                        {step.title}
                                    </h3>

                                    <p className='mt-4 max-w-xs text-[15px] leading-relaxed text-muted-foreground'>
                                        {step.description}
                                    </p>
                                </Reveal>
                            </li>
                        )
                    })}
                </ol>
            </Container>
        </section>
    )
}
